import type { CalculatorDefinition } from "./calculator-types";

// ─────────────────────────────────────────────────────────────────────────────
// 1. MAKSPULS-KALKULATOR (Tanaka 2001)
// ─────────────────────────────────────────────────────────────────────────────
export const makspulsKalkulator: CalculatorDefinition = {
  id: "makspuls",
  slug: "makspuls-kalkulator",
  title: "Makspuls-kalkulator",
  description: "Beregn din maksimale puls + alle 5 treningssoner basert på HUNT-formelen (211 − 0,64 × alder) fra den norske HUNT-studien.",
  metaTitle: "Makspuls-Kalkulator — Treningssoner & HUNT-formel | CoachGuiden",
  metaDescription: "Gratis makspuls-kalkulator. Få dine 5 treningssoner + 4×4-intervallsonen basert på HUNT-formelen fra norsk forskning — mer presis enn 220 minus alder.",
  focusKeyword: "makspuls kalkulator",
  badge: { text: "HUNT-studien (Nes 2013)", color: "orange" },
  inputs: [
    { id: "age", label: "Alder", type: "number", unit: "år", min: 15, max: 100, defaultValue: 35, required: true },
    { id: "gender", label: "Kjønn", type: "radio", defaultValue: "mann", required: true, options: [
      { label: "Mann", value: "mann" },
      { label: "Kvinne", value: "kvinne" },
    ]},
    { id: "restingHr", label: "Hvilepuls", type: "number", unit: "slag/min", min: 30, max: 100, required: false, helpText: "Måles første gang du står opp om morgenen" },
  ],
  compute: (v) => {
    const age = Number(v.age) || 35;
    // Primærformel: HUNT Fitness Study (Nes 2013), norsk populasjon, SEE ±10,8 slag
    const maxHr = Math.round(211 - 0.64 * age);
    const tanaka = Math.round(208 - 0.7 * age);
    const zones = [
      { label: "Sone 1 — Restitusjon", subtext: "50–60 % av makspuls", description: "Veldig lett. Du kan synge mens du løper.", min: 0.5, max: 0.6, color: "#60a5fa" },
      { label: "Sone 2 — Aerob base", subtext: "60–70 % av makspuls", description: "Lett. Samtale i hele setninger.", min: 0.6, max: 0.7, color: "#4ade80" },
      { label: "Sone 3 — Tempo", subtext: "70–80 % av makspuls", description: "Moderat hard. Korte setninger.", min: 0.7, max: 0.8, color: "#facc15" },
      { label: "Sone 4 — Terskel", subtext: "80–90 % av makspuls", description: "Hard. Bare enkeltord. Holdes 20–40 min.", min: 0.8, max: 0.9, color: "#f97316" },
      { label: "Sone 5 — VO2 Max", subtext: "90–100 % av makspuls", description: "All-out. Korte intervaller (1–4 min).", min: 0.9, max: 1.0, color: "#ef4444" },
    ];
    return {
      primary: {
        label: "Din makspuls",
        value: maxHr,
        unit: "slag/min",
        context: `Beregnet med HUNT-formelen (211 − 0,64 × ${age}) fra den norske HUNT-studien. Estimat for gruppen, ikke en garanti for deg: individuell spredning er ±10–11 slag. Tanaka-formelen gir til sammenligning ${tanaka}.`,
      },
      detailsTitle: "Dine 5 treningssoner",
      details: zones.map((z) => ({
        label: z.label,
        subtext: z.subtext,
        description: z.description,
        value: `${Math.round(maxHr * z.min)}–${Math.round(maxHr * z.max)}`,
        valueUnit: "slag/min",
        color: z.color,
      })).concat([{
        label: "4×4-intervaller (norsk protokoll)",
        subtext: "90–95 % av makspuls",
        description: "4 drag à 4 min med 3 min aktiv pause (~70 %). Protokollen fra Helgerud-studien — se forklaringen under.",
        value: `${Math.round(maxHr * 0.9)}–${Math.round(maxHr * 0.95)}`,
        valueUnit: "slag/min",
        color: "#b45309",
      }]),
    };
  },
  explanation: {
    title: "Hvorfor HUNT-formelen — og hva 4×4-sonen er",
    body: `Den klassiske formelen «220 minus alder» ble laget i 1971 og er ikke vitenskapelig validert. Denne kalkulatoren bruker formelen fra HUNT Fitness Study (Nes et al. 2013), der makspuls ble målt direkte hos 3 320 friske norske voksne: 211 − 0,64 × alder. Tanaka et al. (2001, meta-analyse av 351 studier) landet på lignende tall (208 − 0,7 × alder) — begge treffer bedre enn 220-regelen, som undervurderer makspuls hos voksne over 30.

Viktig forbehold: alle formler estimerer gjennomsnittet. Den individuelle spredningen er ±10–11 slag (standardfeil 10,8 i HUNT-materialet), så din faktiske makspuls kan avvike. Vil du vite den presist, må den måles — f.eks. med en hard, gradvis økende test med pulsklokke, helst med noe erfaring og god helse i bunn.

4×4-sonen: i studien til Helgerud og kolleger (2007) forbedret intervaller på 4 × 4 minutter ved 90–95 % av makspuls (med 3 minutters aktiv pause på ~70 %) det maksimale oksygenopptaket med om lag 7 % på 8 uker hos moderat trente menn — signifikant mer enn samme mengde moderat trening. Din fremgang avhenger av utgangspunkt, restitusjon og etterlevelse.

Kilder: Nes BM, Janszky I, Wisløff U, Støylen A, Karlsen T. «Age-predicted maximal heart rate in healthy subjects: The HUNT Fitness Study.» Scand J Med Sci Sports. 2013;23(6):697–704 · Tanaka H, Monahan KD, Seals DR. «Age-predicted maximal heart rate revisited.» J Am Coll Cardiol. 2001;37(1):153–156 · Helgerud J, et al. «Aerobic high-intensity intervals improve VO2max more than moderate training.» Med Sci Sports Exerc. 2007;39(4):665–671.`,
  },
  emailGate: {
    headline: "Vil du ha resultatene tilsendt?",
    description: "Vi sender en PDF med dine soner + 8-ukers HIIT-program tilpasset din makspuls.",
    buttonText: "Send →",
  },
  cta: {
    headline: "Vil du øke kondisjonen?",
    description: "En treningscoach kan periodisere intervaller, restitusjon og styrke for ditt mål.",
    buttonText: "Finn treningscoach",
    href: "/coacher?category=trening",
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// 2. FETTPROSENT-KALKULATOR (US Navy-metoden)
// ─────────────────────────────────────────────────────────────────────────────
export const fettprosentKalkulator: CalculatorDefinition = {
  id: "fettprosent",
  slug: "fettprosent-kalkulator",
  title: "Fettprosent-kalkulator",
  description: "Beregn din fettprosent med US Navy-metoden — kun målebånd og noen mål kreves.",
  metaTitle: "Fettprosent-Kalkulator — US Navy-Metoden | CoachGuiden",
  metaDescription: "Gratis fettprosent-kalkulator basert på US Navy-metoden. Få estimat + referanseverdier for alder og kjønn.",
  focusKeyword: "fettprosent kalkulator",
  badge: { text: "US Navy-metoden", color: "orange" },
  inputs: [
    { id: "gender", label: "Kjønn", type: "radio", defaultValue: "mann", required: true, options: [
      { label: "Mann", value: "mann" },
      { label: "Kvinne", value: "kvinne" },
    ]},
    { id: "height", label: "Høyde", type: "number", unit: "cm", min: 130, max: 220, defaultValue: 178, required: true },
    { id: "neck", label: "Halsmål", type: "number", unit: "cm", min: 25, max: 60, defaultValue: 38, required: true, helpText: "Mål rundt halsen, like under adamseplet" },
    { id: "waist", label: "Midjemål", type: "number", unit: "cm", min: 50, max: 200, defaultValue: 85, required: true, helpText: "Måles ved navlen, ikke trekk inn magen" },
    { id: "hip", label: "Hoftemål (kun kvinner)", type: "number", unit: "cm", min: 60, max: 160, defaultValue: 95, required: false },
    { id: "age", label: "Alder", type: "number", unit: "år", min: 15, max: 100, defaultValue: 35, required: true },
  ],
  compute: (v) => {
    const gender = v.gender || "mann";
    const height = Number(v.height) || 178;
    const neck = Number(v.neck) || 38;
    const waist = Number(v.waist) || 85;
    const hip = Number(v.hip) || 95;
    const age = Number(v.age) || 35;

    let bodyFat: number;
    if (gender === "mann") {
      bodyFat = 495 / (1.0324 - 0.19077 * Math.log10(waist - neck) + 0.15456 * Math.log10(height)) - 450;
    } else {
      bodyFat = 495 / (1.29579 - 0.35004 * Math.log10(waist + hip - neck) + 0.221 * Math.log10(height)) - 450;
    }
    bodyFat = Math.max(2, Math.min(50, Math.round(bodyFat * 10) / 10));

    // Klassifisering basert på ACE-tabeller
    const categories = gender === "mann"
      ? [
          { max: 5, label: "Essensielt fett (svært lavt)", color: "#3b82f6" },
          { max: 13, label: "Atletisk", color: "#22c55e" },
          { max: 17, label: "Fitness", color: "#84cc16" },
          { max: 24, label: "Akseptabelt", color: "#eab308" },
          { max: 100, label: "Overvektig", color: "#ef4444" },
        ]
      : [
          { max: 13, label: "Essensielt fett (svært lavt)", color: "#3b82f6" },
          { max: 20, label: "Atletisk", color: "#22c55e" },
          { max: 24, label: "Fitness", color: "#84cc16" },
          { max: 31, label: "Akseptabelt", color: "#eab308" },
          { max: 100, label: "Overvektig", color: "#ef4444" },
        ];
    const cat = categories.find((c) => bodyFat <= c.max) || categories[categories.length - 1];

    return {
      primary: {
        label: "Estimert fettprosent",
        value: bodyFat.toFixed(1),
        unit: "%",
        context: `Klassifisering: ${cat.label}. Beregnet med US Navy-metoden — usikkerhet ±3 %.`,
      },
      detailsTitle: "Referanseverdier (ACE)",
      details: categories.map((c, i) => {
        const lower = i === 0 ? 0 : categories[i - 1].max;
        const upper = c.max === 100 ? "+" : c.max;
        return {
          label: c.label,
          value: c.max === 100 ? `${lower}+` : `${lower}–${upper}`,
          valueUnit: "%",
          color: c.color,
          subtext: bodyFat <= c.max && bodyFat > lower ? "← du er her" : undefined,
        };
      }),
    };
  },
  explanation: {
    title: "Hvor presis er denne metoden?",
    body: `US Navy-metoden er basert på omkrets-mål og er validert mot DEXA (gull-standarden) i flere studier — den treffer typisk innenfor ±3 % for friske voksne.

For mer presisjon, vurder:
• DEXA-skann (±1 %) — tilbys ved enkelte helse-sentra og idrettsmedisinske klinikker
• InBody (BIA, ±2–3 %) — finnes på mange treningssentre
• Hudfold-mål med kalipper (±3 %) — krever erfaren utfører

Husk: Trenden over tid betyr mer enn enkelt-målingen. Mål med samme metode hver 6.–8. uke for å se utvikling.`,
  },
  emailGate: {
    headline: "Vil du ha en strukturert plan?",
    description: "Vi sender deg en PDF med kostholdstips, makro-mål og treningsråd basert på din nåværende fettprosent.",
    buttonText: "Send meg planen",
  },
  cta: {
    headline: "Trenger du en plan tilpasset deg?",
    description: "En kostholds- eller treningscoach kan lage et opplegg som matcher din fettprosent, livssituasjon og mål.",
    buttonText: "Finn coach",
    href: "/coacher",
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// 3. SUNN MIDDAG-KALKULATOR (Mifflin-St Jeor + tallerkenmodellen)
// ─────────────────────────────────────────────────────────────────────────────
export const sunnMiddagKalkulator: CalculatorDefinition = {
  id: "sunn-middag",
  slug: "middagskalkulator",
  title: "Middagskalkulator",
  description: "Hvor mye bør middagen din inneholde? Beregn anbefalt kalori- og proteinmengde for middagen basert på kroppen og målet ditt.",
  metaTitle: "Middagskalkulator: kalorier og protein i middagen | CoachGuiden",
  metaDescription: "Gratis middagskalkulator. Få anbefalt kalori- og proteinmengde for middagen din basert på vekt, aktivitet og mål — bygget på tallerkenmodellen.",
  focusKeyword: "middagskalkulator",
  badge: { text: "Mifflin-St Jeor", color: "green" },
  inputs: [
    { id: "gender", label: "Kjønn", type: "radio", defaultValue: "kvinne", required: true, options: [
      { label: "Kvinne", value: "kvinne" },
      { label: "Mann", value: "mann" },
    ]},
    { id: "age", label: "Alder", type: "number", unit: "år", min: 15, max: 100, defaultValue: 35, required: true },
    { id: "height", label: "Høyde", type: "number", unit: "cm", min: 130, max: 220, defaultValue: 170, required: true },
    { id: "weight", label: "Vekt", type: "number", unit: "kg", min: 35, max: 200, defaultValue: 75, required: true },
    { id: "activity", label: "Aktivitetsnivå", type: "select", defaultValue: "moderat", required: true, options: [
      { label: "Stillesittende (lite trening)", value: "stille" },
      { label: "Lett aktiv (1–2 økter/uke)", value: "lett" },
      { label: "Moderat aktiv (3–4 økter/uke)", value: "moderat" },
      { label: "Svært aktiv (5–6 økter/uke)", value: "hoy" },
      { label: "Ekstremt aktiv (fysisk jobb + trening)", value: "ekstrem" },
    ]},
    { id: "goal", label: "Mål", type: "radio", defaultValue: "vedlikehold", required: true, options: [
      { label: "Gå ned i vekt", value: "ned" },
      { label: "Vedlikeholde", value: "vedlikehold" },
      { label: "Bygge muskler", value: "opp" },
    ]},
  ],
  compute: (v) => {
    const gender = v.gender || "kvinne";
    const age = Number(v.age) || 35;
    const height = Number(v.height) || 170;
    const weight = Number(v.weight) || 75;
    const activity = v.activity || "moderat";
    const goal = v.goal || "vedlikehold";

    // BMR — Mifflin-St Jeor (1990)
    const bmr = gender === "mann"
      ? 10 * weight + 6.25 * height - 5 * age + 5
      : 10 * weight + 6.25 * height - 5 * age - 161;
    const actMap: Record<string, number> = { stille: 1.2, lett: 1.375, moderat: 1.55, hoy: 1.725, ekstrem: 1.9 };
    const tdee = bmr * (actMap[activity] || 1.55);

    // Måljustering — vektnedgang: −500 kcal (NHI), muskelbygging: +300 kcal
    const goalAdj: Record<string, number> = { ned: -500, vedlikehold: 0, opp: 300 };
    const dailyKcal = Math.round(tdee + (goalAdj[goal] || 0));

    // Middagen ≈ 35 % av dagsbehovet (frokost 25 / lunsj 30 / middag 35 / snacks 10)
    const dinnerKcal = Math.round(dailyKcal * 0.35 / 10) * 10;

    // Protein: g/kg per mål, middagens andel (35 %)
    const proteinPerKg: Record<string, number> = { ned: 2.0, vedlikehold: 1.6, opp: 1.8 };
    const dailyProtein = Math.round(weight * (proteinPerKg[goal] || 1.6));
    const dinnerProtein = Math.round(dailyProtein * 0.35);

    const goalLabel: Record<string, string> = { ned: "vektnedgang", vedlikehold: "vedlikehold", opp: "muskelbygging" };

    return {
      primary: {
        label: "Anbefalt middag",
        value: dinnerKcal,
        unit: "kcal",
        context: `Omtrent 35 % av ditt daglige behov på ${dailyKcal} kcal (mål: ${goalLabel[goal]}). Et utgangspunkt — juster etter sult, aktivitet og hvordan kroppen responderer.`,
      },
      detailsTitle: "Slik setter du sammen middagen",
      details: [
        { label: "Daglig kaloribehov", value: String(dailyKcal), valueUnit: "kcal", description: goal === "ned" ? "Inkluderer 500 kcal underskudd for vektnedgang" : goal === "opp" ? "Inkluderer 300 kcal overskudd for muskelbygging" : "For å holde vekten stabil", color: "#1c1917" },
        { label: "Protein i middagen", value: `${dinnerProtein}`, valueUnit: "g", subtext: "minst 20–30 g for muskelproteinsyntese", description: "Fisk, kylling, egg, kjøtt eller belgvekster", color: "#16a34a" },
        { label: "Tallerkenmodellen", value: "½ · ¼ · ¼", subtext: "grønt · karbo · protein", description: "Fyll halve tallerkenen med grønnsaker, en fjerdedel karbohydrater og en fjerdedel proteinrik mat", color: "#f97316" },
      ],
    };
  },
  explanation: {
    title: "Hvordan regner kalkulatoren?",
    body: `Kalkulatoren beregner først ditt daglige energibehov med Mifflin-St Jeor-formelen (1990) — den mest treffsikre formelen for hvilebehov hos friske voksne — ganget med aktivitetsnivået ditt.

Deretter justeres det etter målet: for vektnedgang trekkes 500 kcal fra (NHI anbefaler minst 500 kcal under behovet), og for muskelbygging legges 300 kcal til.

Middagen settes til ca. 35 % av dagsbehovet, basert på en typisk fordeling der frokost utgjør 25 %, lunsj 30 %, middag 35 % og mellommåltider 10 %.

Tallene er et utgangspunkt, ikke en fasit. Sult, søvn, aktivitet og hvordan du responderer betyr mer enn en formel på desimalen.`,
  },
  emailGate: {
    headline: "Vil du ha resultatet + en ukesmeny tilsendt?",
    description: "Vi sender deg middagsmålene dine pluss en enkel mal for å sette sammen sunne middager hele uka.",
    buttonText: "Send meg det",
  },
  cta: {
    headline: "Vil du ha en kostholdsplan som passer deg?",
    description: "En kostholdscoach kan lage et opplegg tilpasset målet ditt, hverdagen din og maten du faktisk liker.",
    buttonText: "Finn kostholdscoach",
    href: "/coacher?category=kosthold",
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// 4. 1RM- OG STARTVEKT-KALKULATOR (Epley 1985 + Brzycki 1993, validert DiStasio 2014)
//    To spor: kjenner du en rep-max estimeres 1RM; er du helt fersk beregnes
//    ALDRI 1RM — du får konservative startvekter + lineær progresjon i stedet.
// ─────────────────────────────────────────────────────────────────────────────
const OVELSE_START: Record<string, { label: string; mann: string; kvinne: string; underkropp: boolean }> = {
  kneboy: { label: "Knebøy", mann: "tom stang (20 kg)", kvinne: "tom stang (15–20 kg)", underkropp: true },
  benkpress: { label: "Benkpress", mann: "tom stang (20 kg)", kvinne: "15-kg-stang eller 2 × 8 kg manualer", underkropp: false },
  markloft: { label: "Markløft", mann: "40 kg (stang + to 10-kg-skiver)", kvinne: "30 kg (bruk lettvektskiver i full høyde)", underkropp: true },
  press: { label: "Stående press", mann: "tom stang (20 kg)", kvinne: "15-kg-stang eller 2 × 6 kg manualer", underkropp: false },
  roing: { label: "Stangroing", mann: "30 kg", kvinne: "20 kg", underkropp: false },
};

export const enRmKalkulator: CalculatorDefinition = {
  id: "1rm-startvekt",
  slug: "1rm-kalkulator",
  title: "1RM- og startvekt-kalkulator",
  description: "Estimer din 1RM fra et sett på 2–10 reps (Epley + Brzycki), eller få trygge startvekter hvis du er helt fersk — pluss arbeidsvekter for programmene våre.",
  metaTitle: "1RM-Kalkulator — Estimat + Startvekter | CoachGuiden",
  metaDescription: "Gratis 1RM-kalkulator: estimer maks fra 2–10 reps med Epley- og Brzycki-formlene, eller få trygge startvekter som nybegynner. Med arbeidsvekt-tabell.",
  focusKeyword: "1rm kalkulator",
  badge: { text: "Epley + Brzycki (validert)", color: "orange" },
  inputs: [
    { id: "track", label: "Hvor står du?", type: "radio", defaultValue: "kjent", required: true, options: [
      { label: "Jeg kjenner en rep-max (f.eks. 5 reps på 80 kg)", value: "kjent" },
      { label: "Jeg er helt fersk med vekter", value: "fersk" },
    ]},
    { id: "ovelse", label: "Øvelse", type: "select", defaultValue: "kneboy", required: true, options: [
      { label: "Knebøy", value: "kneboy" },
      { label: "Benkpress", value: "benkpress" },
      { label: "Markløft", value: "markloft" },
      { label: "Stående press", value: "press" },
      { label: "Stangroing", value: "roing" },
    ]},
    { id: "weight", label: "Vekt du løftet", type: "number", unit: "kg", min: 20, max: 400, defaultValue: 80, required: false, helpText: "Kun hvis du kjenner en rep-max" },
    { id: "reps", label: "Antall reps", type: "number", unit: "reps", min: 2, max: 10, defaultValue: 5, required: false, helpText: "2–10 reps — over 10 blir estimatet upålitelig, derfor stopper kalkulatoren der" },
    { id: "gender", label: "Kjønn", type: "radio", defaultValue: "mann", required: false, options: [
      { label: "Mann", value: "mann" },
      { label: "Kvinne", value: "kvinne" },
    ], helpText: "Brukes kun for startvekt-forslaget til deg som er fersk" },
  ],
  compute: (v) => {
    const track = v.track || "kjent";
    const ovelse = OVELSE_START[v.ovelse as string] || OVELSE_START.kneboy;

    if (track === "fersk") {
      const gender = v.gender === "kvinne" ? "kvinne" : "mann";
      const start = ovelse[gender];
      const inc = ovelse.underkropp ? "5 kg" : "2,5 kg";
      return {
        primary: {
          label: `Startvekt — ${ovelse.label}`,
          value: start,
          context: "Et grovt utgangspunkt — ikke en fasit. Som fersk skal du IKKE teste 1RM: de første ukene handler om teknikk, og vekten under er lett med vilje. Kjennes den for lett, er den riktig.",
        },
        detailsTitle: "Slik progredierer du som fersk",
        details: [
          { label: "Første økt", value: start, description: "Fokus på teknikk — kontrollert ned, bestemt opp. Film deg selv eller be noen se på.", color: "#22c55e" },
          { label: "Hver økt etterpå", value: `+ ${inc}`, description: `Legg på ${inc} per økt så lenge alle reps sitter med god teknikk. Bommer du tre økter på rad: ta 10 % av og bygg opp igjen.`, color: "#f97316" },
          { label: "1RM-testing", value: "Vent", description: "Estimér heller fra et 3–5-repssett når du har trent noen måneder — da kan du bruke det andre sporet i denne kalkulatoren.", color: "#78716c" },
        ],
      };
    }

    const w = Number(v.weight) || 80;
    const reps = Math.min(10, Math.max(2, Number(v.reps) || 5));
    const epley = w * (1 + reps / 30);
    const brzycki = w / (1.0278 - 0.0278 * reps);
    const oneRm = Math.round(((epley + brzycki) / 2) / 2.5) * 2.5;
    const pct = (p: number) => `${Math.round((oneRm * p) / 2.5) * 2.5} kg`;
    return {
      primary: {
        label: `Estimert 1RM — ${ovelse.label}`,
        value: oneRm,
        unit: "kg",
        context: `Snitt av Epley (${Math.round(epley)} kg) og Brzycki (${Math.round(brzycki)} kg) fra ${w} kg × ${reps} reps, avrundet til nærmeste 2,5 kg. Et estimat for planlegging — ikke en invitasjon til å teste maks i dag.`,
      },
      detailsTitle: "Arbeidsvektene dine (avrundet til 2,5 kg)",
      details: [
        { label: "Treningsmaks (90 %)", value: pct(0.9), subtext: "grunnlaget i Bølgeprogrammet", description: "Prosentbaserte program regner av denne — aldri av ekte 1RM", color: "#1c1917" },
        { label: "Tungt (85 %)", value: pct(0.85), subtext: "typisk 3–5 reps", description: "Tunge arbeidssett med full kontroll", color: "#ef4444" },
        { label: "Moderat tungt (80 %)", value: pct(0.8), subtext: "typisk 5–6 reps", description: "Klassisk 5×5-sone", color: "#f97316" },
        { label: "Moderat (75 %)", value: pct(0.75), subtext: "typisk 6–8 reps", description: "Volumarbeid med god teknikk-margin", color: "#facc15" },
        { label: "Lett–moderat (65–70 %)", value: `${pct(0.65)}–${pct(0.7)}`, subtext: "typisk 8–12 reps", description: "Oppvarmingens siste steg og lettere volumøkter", color: "#4ade80" },
      ],
    };
  },
  explanation: {
    title: "Formlene — og hvorfor 2–10 reps er grensen",
    body: `Kalkulatoren bruker snittet av to veletablerte formler: Epley (1985): 1RM = vekt × (1 + reps/30), og Brzycki (1993): 1RM = vekt / (1,0278 − 0,0278 × reps).

I en valideringsstudie på knebøy hos collegeutøvere (DiStasio 2014, Southern Illinois University) traff Epley i snitt innen +2,7 kg fra en 3RM-test, og Brzycki innen −3,1 kg fra en 5RM-test — avvik så små at de ikke var statistisk signifikante. Over ca. 10 reps blir alle slike formler upålitelige; derfor tar kalkulatoren ikke imot mer.

Er du helt fersk, er et 1RM-estimat verken trygt eller nyttig — teknikken er ikke satt, og fremgangen kommer uansett raskt med lette vekter og jevn progresjon. Derfor får du startvekter og en progresjonsregel i stedet.

Estimatet er for planlegging: programmene våre regner arbeidsvekter i prosent av dette tallet (Bølgeprogrammet bruker en treningsmaks på 90 %). Din faktiske dagsform varierer — la loggen, ikke kalkulatoren, avgjøre neste steg.`,
  },
  cta: {
    headline: "Vil du ha et program som bruker tallene dine?",
    description: "Styrkeprogrammene våre (Stang 5×5, Texas-metoden, Bølgeprogrammet m.fl.) regner arbeidsvektene i prosent — dette estimatet er alt du trenger for å starte.",
    buttonText: "Se programmene",
    href: "/butikk",
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// 5. PACE-KALKULATOR (jevn fart + Riegel 1981)
//    To spor: måltid → nødvendig fart per km med mellomtider, eller kjent
//    løpstid → prognose for andre distanser med Riegel-formelen (eksponent 1,06).
// ─────────────────────────────────────────────────────────────────────────────
const PACE_DISTANSER: { value: string; label: string; km: number }[] = [
  { value: "5", label: "5 km", km: 5 },
  { value: "10", label: "10 km", km: 10 },
  { value: "21.1", label: "Halvmaraton (21,1 km)", km: 21.0975 },
  { value: "42.2", label: "Maraton (42,2 km)", km: 42.195 },
];

// Sekunder → "mm:ss" eller "t:mm:ss"
const fmtTid = (sec: number): string => {
  const s = Math.round(sec);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  return h > 0
    ? `${h}:${String(m).padStart(2, "0")}:${String(ss).padStart(2, "0")}`
    : `${m}:${String(ss).padStart(2, "0")}`;
};

export const paceKalkulator: CalculatorDefinition = {
  id: "pace",
  slug: "pace-kalkulator",
  title: "Pace-kalkulator for løping",
  description: "Finn farten du må holde for måltiden din — med mellomtider underveis. Eller bruk en kjent løpstid til å estimere andre distanser med Riegel-formelen.",
  metaTitle: "Pace-kalkulator: fart per km + Riegel-prognose | CoachGuiden",
  metaDescription: "Gratis pace-kalkulator: farten du må holde for måltiden på 5 km, 10 km, halvmaraton og maraton — med mellomtider og Riegel-prognose for nye distanser.",
  focusKeyword: "pace kalkulator",
  badge: { text: "Riegel-formelen (1981)", color: "orange" },
  inputs: [
    { id: "mode", label: "Hva vil du regne ut?", type: "radio", defaultValue: "maaltid", required: true, options: [
      { label: "Måltid → nødvendig fart", value: "maaltid" },
      { label: "Kjent tid → andre distanser", value: "prognose" },
    ]},
    { id: "distanse", label: "Distanse", type: "select", defaultValue: "10", required: true, options: [
      ...PACE_DISTANSER.map((d) => ({ label: d.label, value: d.value })),
      { label: "Egendefinert", value: "egen" },
    ], helpText: "I prognose-sporet: distansen du kjenner tiden din på" },
    { id: "egenKm", label: "Egendefinert distanse", type: "number", unit: "km", min: 1, max: 100, step: 0.1, required: false, helpText: "Kun hvis du valgte «Egendefinert» over" },
    { id: "timer", label: "Tid — timer", type: "number", unit: "t", min: 0, max: 23, defaultValue: 0, required: true },
    { id: "minutter", label: "Tid — minutter", type: "number", unit: "min", min: 0, max: 59, defaultValue: 55, required: true },
    { id: "sekunder", label: "Tid — sekunder", type: "number", unit: "sek", min: 0, max: 59, defaultValue: 0, required: true },
  ],
  compute: (v) => {
    const mode = v.mode || "maaltid";
    const distSel = String(v.distanse || "10");
    const kjent = PACE_DISTANSER.find((d) => d.value === distSel);
    const dist = distSel === "egen" ? Number(v.egenKm) || 0 : kjent ? kjent.km : 10;
    const distLabel = kjent ? kjent.label : `${String(dist).replace(".", ",")} km`;
    const totalSec = (Number(v.timer) || 0) * 3600 + (Number(v.minutter) || 0) * 60 + (Number(v.sekunder) || 0);

    if (totalSec <= 0 || dist <= 0) {
      return {
        primary: {
          label: "Mangler tid eller distanse",
          value: "–",
          context: "Fyll inn distanse og tid over, så regner kalkulatoren ut farten.",
        },
      };
    }

    const paceSec = totalSec / dist;
    const kmt = (dist / (totalSec / 3600)).toFixed(1).replace(".", ",");

    if (mode === "prognose") {
      // Riegel (1981): T2 = T1 × (D2/D1)^1,06
      const targets = PACE_DISTANSER.filter((t) => Math.abs(t.km - dist) > 0.3);
      return {
        primary: {
          label: `Din fart — ${distLabel}`,
          value: fmtTid(paceSec),
          unit: "min/km",
          context: `Utgangspunkt: ${distLabel} på ${fmtTid(totalSec)} (${kmt} km/t). Prognosene under bruker Riegel-formelen T2 = T1 × (D2/D1)^1,06 — og forutsetter at du er tilsvarende trent for distansen, se forklaringen.`,
        },
        detailsTitle: "Riegel-prognoser for andre distanser",
        details: targets.map((t) => {
          const t2 = totalSec * Math.pow(t.km / dist, 1.06);
          return {
            label: t.label,
            value: fmtTid(t2),
            subtext: `${fmtTid(t2 / t.km)} min/km`,
            description: t.km > dist
              ? "Forutsetter at du har trent langturer for distansen"
              : "Kortere distanse — prognosen er som regel mest treffsikker nedover",
            color: t.km > dist ? "#f97316" : "#4ade80",
          };
        }),
      };
    }

    // Måltid-sporet: mellomtider per km (≤ 10 km) eller per 5 km (lengre løp)
    const punkter: number[] = [];
    if (dist <= 10.5) {
      for (let k = 1; k < dist; k++) punkter.push(k);
    } else {
      for (let k = 5; k < dist - 0.3; k += 5) punkter.push(k);
    }
    return {
      primary: {
        label: `Nødvendig fart — ${distLabel}`,
        value: fmtTid(paceSec),
        unit: "min/km",
        context: `For å løpe ${distLabel} på ${fmtTid(totalSec)} må du holde ${fmtTid(paceSec)} min/km jevnt — det tilsvarer ${fmtTid(paceSec * 10)} per mil og ${kmt} km/t. Mellomtidene under forutsetter helt jevn fart.`,
      },
      detailsTitle: "Mellomtider ved jevn fart",
      details: [
        { label: "Fart per mil", value: fmtTid(paceSec * 10), valueUnit: "min/mil", color: "#1c1917" },
        { label: "Hastighet", value: kmt, valueUnit: "km/t", color: "#1c1917" },
        ...punkter.map((k) => ({
          label: `Mellomtid ${k} km`,
          value: fmtTid(paceSec * k),
          color: "#4ade80",
        })),
        { label: `Mål — ${distLabel}`, value: fmtTid(totalSec), color: "#f97316" },
      ],
    };
  },
  explanation: {
    title: "Riegel-formelen — og hva prognosene forutsetter",
    body: `Fart-delen er ren aritmetikk: tiden delt på distansen gir fart per kilometer, og mellomtidene forutsetter at du holder nøyaktig samme fart hele veien. I praksis åpner mange litt for fort — en jevn eller svakt økende fart (negativ splitt) er som regel en tryggere plan enn å «legge i banken» tidlig.

Prognose-sporet bruker Riegel-formelen: T2 = T1 × (D2/D1)^1,06, publisert av den amerikanske ingeniøren og løperen Peter Riegel i 1981. Eksponenten 1,06 fanger opp at farten faller litt for hver gang distansen dobles. Formelen er en veletablert tommelfingerregel i løpemiljøet — ikke en garanti.

Det viktigste forbeholdet: prognosen forutsetter at du er tilsvarende trent for distansen den peker på. En sterk 5 km-tid gir bare en realistisk maratonprognose hvis du faktisk har lagt ned langturene og volumet et maratonløp krever. Uten det spesifikke grunnlaget vil formelen typisk være for optimistisk på de lange distansene.

Kilde: Riegel, P.S. (1981). «Athletic Records and Human Endurance». American Scientist, 69(3), 285–290.`,
  },
  related: [
    { label: "Løpeprogram for nybegynnere (8 uker)", href: "/treningsprogrammer/lopeprogram-nybegynner" },
    { label: "Alle treningsprogrammer — inkludert 10 km-programmene", href: "/treningsprogrammer" },
    { label: "Tredemølle til hjemmebruk: slik velger du riktig", href: "/ressurser/artikler/tredemolle-guide" },
    { label: "VO2 Max-kalkulator", href: "/verktoy/vo2-max-kalkulator" },
  ],
  emailGate: {
    headline: "Vil du ha mellomtidene tilsendt?",
    description: "Vi sender deg farten og mellomtidene dine på e-post, så du har dem tilgjengelig på løpsdagen.",
    buttonText: "Send →",
  },
  cta: {
    headline: "Vil du trene mot en konkret tid?",
    description: "Treningsprogrammene våre for løping bygger deg gradvis opp mot distansen — med økter du kan planlegge med farten herfra.",
    buttonText: "Se løpeprogrammene",
    href: "/treningsprogrammer",
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// 6. SØVNKALKULATOR (90-minutters sykluser + 15 min innsovning)
//    Ærlig ramme: 90 min er et gjennomsnitt (individuelt ca. 70–120 min) —
//    planleggingsverktøy, ikke medisin. Råd fra Helsenorge i forklaringen.
// ─────────────────────────────────────────────────────────────────────────────
const SOVN_SYKLUS_MIN = 90;
const SOVN_INNSOVNING_MIN = 15;

// Minutter siden midnatt → "07:00" (håndterer negative verdier / over døgnet)
const fmtKlokke = (totalMin: number): string => {
  const m = ((Math.round(totalMin) % 1440) + 1440) % 1440;
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
};

export const sovnKalkulator: CalculatorDefinition = {
  id: "sovn",
  slug: "sovnkalkulator",
  title: "Søvnkalkulator: når bør du legge deg?",
  description: "Legg inn når du må opp, så foreslår kalkulatoren leggetider basert på søvnsykluser på ca. 90 minutter — eller finn vekketider hvis du legger deg nå.",
  metaTitle: "Søvnkalkulator: Når bør du legge deg? | CoachGuiden",
  metaDescription: "Gratis søvnkalkulator: legg inn vekketiden din og få foreslåtte leggetider basert på søvnsykluser på ca. 90 minutter — 4, 5 eller 6 sykluser pluss innsovning.",
  focusKeyword: "søvnkalkulator",
  badge: { text: "90-minutters søvnsykluser", color: "violet" },
  inputs: [
    { id: "mode", label: "Hva vil du finne?", type: "radio", defaultValue: "vekketid", required: true, options: [
      { label: "Jeg vet når jeg må opp", value: "vekketid" },
      { label: "Jeg legger meg nå", value: "naa" },
    ]},
    { id: "vekkeTime", label: "Vekketid — time", type: "number", unit: "kl", min: 0, max: 23, defaultValue: 7, required: true, helpText: "Brukes kun i «Jeg vet når jeg må opp»" },
    { id: "vekkeMinutt", label: "Vekketid — minutter", type: "number", unit: "min", min: 0, max: 59, step: 5, defaultValue: 0, required: true },
  ],
  compute: (v) => {
    const mode = v.mode || "vekketid";
    const sykluser = [6, 5, 4];
    const sovnTimer = (n: number) => `${(n * SOVN_SYKLUS_MIN / 60).toFixed(1).replace(".", ",").replace(",0", "")} timer søvn`;

    if (mode === "naa") {
      const now = new Date();
      const start = now.getHours() * 60 + now.getMinutes() + SOVN_INNSOVNING_MIN;
      return {
        primary: {
          label: "Forslag til vekketid (5 sykluser)",
          value: fmtKlokke(start + 5 * SOVN_SYKLUS_MIN),
          context: `Legger du deg nå (kl. ${fmtKlokke(start - SOVN_INNSOVNING_MIN)}), gir dette 5 søvnsykluser à ca. 90 minutter — 7,5 timer søvn — pluss 15 minutter innsovningstid. Husk: 90 minutter er et gjennomsnitt, se forklaringen.`,
        },
        detailsTitle: "Vekketider etter antall sykluser",
        details: sykluser.map((n) => ({
          label: `${n} sykluser`,
          subtext: sovnTimer(n),
          value: fmtKlokke(start + n * SOVN_SYKLUS_MIN),
          description: n === 6 ? "Romslig natt — innenfor anbefalingen på 7–9 timer for voksne" : n === 5 ? "7,5 timer — innenfor anbefalingen for de fleste voksne" : "6 timer — i knappeste laget som fast rutine",
          color: n === 6 ? "#4ade80" : n === 5 ? "#facc15" : "#f97316",
        })),
      };
    }

    const wake = (Number(v.vekkeTime) || 0) * 60 + (Number(v.vekkeMinutt) || 0);
    return {
      primary: {
        label: "Forslag til leggetid (5 sykluser)",
        value: fmtKlokke(wake - (5 * SOVN_SYKLUS_MIN + SOVN_INNSOVNING_MIN)),
        context: `Skal du opp kl. ${fmtKlokke(wake)}, gir denne leggetiden 5 søvnsykluser à ca. 90 minutter — 7,5 timer søvn — pluss 15 minutter innsovningstid. Husk: 90 minutter er et gjennomsnitt, se forklaringen.`,
      },
      detailsTitle: "Leggetider etter antall sykluser",
      details: sykluser.map((n) => ({
        label: `${n} sykluser`,
        subtext: sovnTimer(n),
        value: fmtKlokke(wake - (n * SOVN_SYKLUS_MIN + SOVN_INNSOVNING_MIN)),
        description: n === 6 ? "Romslig natt — innenfor anbefalingen på 7–9 timer for voksne" : n === 5 ? "7,5 timer — innenfor anbefalingen for de fleste voksne" : "6 timer — i knappeste laget som fast rutine",
        color: n === 6 ? "#4ade80" : n === 5 ? "#facc15" : "#f97316",
      })),
    };
  },
  explanation: {
    title: "Ærlig talt: 90 minutter er et gjennomsnitt",
    body: `Kalkulatoren regner med søvnsykluser på 90 minutter pluss 15 minutter innsovningstid, og foreslår tider som gir 4–6 hele sykluser. Tanken bak er at det kjennes lettere å våkne på slutten av en syklus, i lett søvn, enn midt i dyp søvn.

Men vær klar over begrensningen: 90 minutter er et gjennomsnitt og en tommelfingerregel. Sykluslengden varierer fra person til person — omtrent 70 til 120 minutter — og endrer seg dessuten utover natten. Kalkulatoren treffer derfor ikke syklusene dine på minuttet; den er et planleggingsverktøy for å finne en fornuftig leggetid, ikke medisin. Har du vedvarende søvnproblemer, bør du snakke med fastlegen din.

Det som er bedre dokumentert enn syklus-timing, er søvnvanene rundt: Helsenorge råder til å ha det mørkt, stille og ikke over 18 grader på soverommet, legge deg til samme tid hver dag og holde kvelden skjermfri. En fast vekketid — også i helgene — gjør mer for søvnen enn å treffe en syklus perfekt.

Kilde: Helsenorge — «Råd for bedre søvn». https://www.helsenorge.no/sovnproblemer/rad-for-bedre-sovn/`,
  },
  related: [
    { label: "Sove bedre: 8 evidensbaserte teknikker som virker", href: "/ressurser/artikler/sove-bedre" },
    { label: "10 søvnhygiene-tips som faktisk virker", href: "/ressurser/artikler/sovnhygiene-tips" },
    { label: "Søvnproblemer: årsaker, tiltak og når du bør søke hjelp", href: "/ressurser/artikler/sovnproblemer-guide" },
    { label: "Sovemaske: virker det, og hvordan velger du riktig?", href: "/ressurser/artikler/sovemaske-guide" },
  ],
  cta: {
    headline: "Sliter du med å få rutinen til å sitte?",
    description: "En coach kan hjelpe deg å bygge kveldsrutiner og vaner som faktisk holder — søvn er ofte det første som løsner.",
    buttonText: "Finn coach",
    href: "/coacher?category=mental-helse",
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// 7. PROTEINBEHOV-KALKULATOR (tall og kilder fra proteinbehov-guiden)
//    Gir alltid et intervall, aldri ett eksakt tall. Nivåene og g/kg-spennene
//    er identiske med /ressurser/artikler/proteinbehov-guide — ikke endre her
//    uten å endre der.
// ─────────────────────────────────────────────────────────────────────────────
const PROTEIN_NIVAER: Record<string, { label: string; min: number; max: number; beskrivelse: string }> = {
  lite: { label: "Lite aktiv", min: 1.0, max: 1.2, beskrivelse: "Lite aktiv voksen — over Helsedirektoratets minimum, men uten treningspåslag" },
  moderat: { label: "Moderat aktiv (trener 2–3x/uke)", min: 1.4, max: 1.6, beskrivelse: "Moderat aktiv — trener 2–3 ganger i uka" },
  aktiv: { label: "Aktiv (trener 4–6x/uke)", min: 1.6, max: 2.0, beskrivelse: "Aktiv — trener 4–6 ganger i uka" },
  styrke: { label: "Styrketrening / muskelbygging", min: 1.8, max: 2.2, beskrivelse: "Styrketrening og muskelbygging — over 2,2 g/kg gir svært liten ekstra effekt" },
  ned: { label: "Vektnedgang (bevare muskelmasse)", min: 2.0, max: 2.4, beskrivelse: "Vektnedgang i kaloriunderskudd — høyere inntak for å bevare muskelmasse" },
};

export const proteinbehovKalkulator: CalculatorDefinition = {
  id: "proteinbehov",
  slug: "proteinbehov-kalkulator",
  title: "Proteinbehov-kalkulator",
  description: "Hvor mye protein trenger du per dag? Få et ærlig intervall i gram basert på vekt og aktivitetsnivå — med tall fra idrettsernæringsforskningen.",
  metaTitle: "Proteinbehov-Kalkulator — Gram protein per dag | CoachGuiden",
  metaDescription: "Gratis proteinbehov-kalkulator: få dagsbehovet ditt i gram som intervall, basert på vekt og aktivitetsnivå — fra 1,0 til 2,4 g/kg avhengig av mål.",
  focusKeyword: "proteinbehov kalkulator",
  badge: { text: "ISSN (Jager 2017)", color: "green" },
  inputs: [
    { id: "weight", label: "Vekt", type: "number", unit: "kg", min: 35, max: 200, defaultValue: 75, required: true },
    { id: "nivaa", label: "Aktivitetsnivå / mål", type: "select", defaultValue: "moderat", required: true, options: [
      { label: "Lite aktiv", value: "lite" },
      { label: "Moderat aktiv (trener 2–3x/uke)", value: "moderat" },
      { label: "Aktiv (trener 4–6x/uke)", value: "aktiv" },
      { label: "Styrketrening / muskelbygging", value: "styrke" },
      { label: "Vektnedgang (bevare muskelmasse)", value: "ned" },
    ]},
  ],
  compute: (v) => {
    const weight = Number(v.weight) || 75;
    const nivaa = PROTEIN_NIVAER[v.nivaa as string] || PROTEIN_NIVAER.moderat;
    const lo = Math.round(weight * nivaa.min);
    const hi = Math.round(weight * nivaa.max);
    const fmtGkg = (n: number) => n.toFixed(1).replace(".", ",");

    return {
      primary: {
        label: `Ditt proteinbehov — ${nivaa.label.toLowerCase()}`,
        value: `${lo}–${hi}`,
        unit: "g/dag",
        context: `Basert på ${fmtGkg(nivaa.min)}–${fmtGkg(nivaa.max)} g per kilo kroppsvekt. Det finnes ikke ett eksakt tall — behovet er et spenn, og hvor i spennet du bør ligge avhenger av treningsmengde og totalt kosthold.`,
      },
      detailsTitle: "Slik leser du tallene",
      details: [
        {
          label: "Helsedirektoratets minimum",
          value: String(Math.round(weight * 0.8)),
          valueUnit: "g",
          subtext: "0,8 g/kg",
          description: "Minimumsnivå for å unngå mangel — ikke et optimalt nivå",
          color: "#78716c",
        },
        {
          label: "Ditt intervall",
          value: `${fmtGkg(nivaa.min)}–${fmtGkg(nivaa.max)}`,
          valueUnit: "g/kg",
          description: nivaa.beskrivelse,
          color: "#16a34a",
        },
        {
          label: "Per måltid (4 måltider)",
          value: `${Math.round(lo / 4)}–${Math.round(hi / 4)}`,
          valueUnit: "g",
          description: "Jevn fordeling gjennom dagen er optimalt — men timing betyr mindre enn totalt inntak",
          color: "#f97316",
        },
      ],
    };
  },
  explanation: {
    title: "Hvorfor et intervall — og hvor tallene kommer fra",
    body: `Det finnes ikke ett riktig proteintall — derfor gir kalkulatoren alltid et spenn. Nivåene er de samme som i proteinbehov-guiden vår: lite aktiv 1,0–1,2 g/kg, moderat aktiv (2–3 økter/uke) 1,4–1,6, aktiv (4–6 økter/uke) 1,6–2,0, styrketrening og muskelbygging 1,8–2,2, og vektnedgang 2,0–2,4 g/kg for å bevare muskelmasse i kaloriunderskudd.

Helsedirektoratets anbefaling på 0,8 g/kg er et minimumsnivå for å unngå mangel — ikke et optimalt nivå for deg som trener. For muskelbygging viser forskningen at over ca. 2,2 g/kg gir svært liten ekstra effekt.

To nyanser fra forskningen: Timing betyr mindre enn totalt inntak — du trenger ikke proteinshake innen 30 minutter etter trening så lenge dagsinntaket er på plass, selv om jevn fordeling gjennom dagen fortsatt er optimalt. Og for friske mennesker finnes det ingen evidens for at høyt proteininntak skader nyrene — studier med inntak opp til 3,5 g/kg over lengre tid viser ingen nyrepåvirkning.

Kilder: Jager, R. et al. (2017). «International Society of Sports Nutrition Position Stand: protein and exercise». JISSN, 14(20) · Antonio, J. et al. (2016). «A High Protein Diet Has No Harmful Effects: A One-Year Crossover Study in Resistance-Trained Males». Journal of Nutrition and Metabolism · Trommelen, J. et al. (2023). «The anabolic response to protein ingestion during recovery from exercise has no upper limit». Cell Reports Medicine, 4(12).`,
  },
  related: [
    { label: "Hvor mye protein trenger du? Komplett guide med matkilder", href: "/ressurser/artikler/proteinbehov-guide" },
    { label: "Proteinrik mat: 30 matvarer med mest protein", href: "/oppskrifter/proteinrik-mat-guide" },
    { label: "Proteinrik middag: beste kilder og enkle oppskrifter", href: "/ressurser/artikler/proteinrik-middag" },
    { label: "Middagskalkulator: kalorier og protein i middagen", href: "/verktoy/middagskalkulator" },
  ],
  emailGate: {
    headline: "Vil du ha resultatet + matkilder tilsendt?",
    description: "Vi sender deg proteinintervallet ditt sammen med en oversikt over de beste proteinkildene i norske butikker.",
    buttonText: "Send meg det",
  },
  cta: {
    headline: "Vil du ha et kosthold som treffer behovet?",
    description: "En kostholdscoach kan sette sammen måltider som dekker proteinbehovet ditt — med mat du faktisk liker.",
    buttonText: "Finn kostholdscoach",
    href: "/coacher?category=kosthold",
  },
};

export const CALCULATORS: CalculatorDefinition[] = [
  makspulsKalkulator,
  fettprosentKalkulator,
  sunnMiddagKalkulator,
  enRmKalkulator,
  paceKalkulator,
  sovnKalkulator,
  proteinbehovKalkulator,
];

export function getCalculatorBySlug(slug: string): CalculatorDefinition | undefined {
  return CALCULATORS.find((c) => c.slug === slug);
}

export function getCalculatorSlugs(): string[] {
  return CALCULATORS.map((c) => c.slug);
}
